import { useEffect, useState, useCallback, useMemo } from 'react';
import './UpcomingEvents.css';
import BidCard from './BidCard';
import { Link } from 'react-router-dom';
import UpcomingEventsCarousel from './UpcomingEventsCarousel';
import axios from 'axios';
import BidCardSkeleton from './BidCardSkeleton';
import { API_BASE_URL } from '../../utils';
import noUpcomingEvent from '../image/no-upcoming-events.svg';
import loginBanner from '../image/loginPromotion.jpg';
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import BidWaitingTimer from './BidWaitingTimer';
import LoginRequiredState from '../upcomingPlanComp/LoginRequiredState';
import InvitedState from '../upcomingPlanComp/InvitedState';
import AfterCounterBidWaiting from '../upcomingPlanComp/AfterCounterBidWaiting';
import CounterBidReject from '../upcomingPlanComp/CounterBidReject';
import WaitingForConformationState from '../upcomingPlanComp/WaitingForConformationState';
import ConfirmByClubAfterPayment from '../upcomingPlanComp/ConfirmByClubAfterPayment';
import ReviewState from '../upcomingPlanComp/ReviewState';
import AddGuestAfterMenuAdded from '../upcomingPlanComp/AddGuestAfterMenuAdded';
import GuestListAddedMenuPanding from '../upcomingPlanComp/GuestListAddedMenuPanding';
import AfterPartyPics from '../upcomingPlanComp/AfterPartyPics';
import MobileStateComponent from '../upcomingPlanComp/MobileStateComponent';

export default function UpcomingEvents({ setModalOpen, setSelecteLead }) {
  const userdata = JSON.parse(localStorage.getItem("userData"));

  const [packageData, setPackageData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [activeLead, setActiveLead] = useState({ id: null, type: null });
  const [isOpenBox, setIsOpenBox] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bids, setBids] = useState([]);

  // Leads without bids
  const packageLeads = useMemo(
    () => packageData.map(({ ...rest }) => rest),
    [packageData]
  );
  const tableLeads = useMemo(
    () => tableData.map(({ ...rest }) => rest),
    [tableData]
  );

  // Combined leads with type tag
  const combinedLeads = useMemo(() => ([
    ...tableLeads.map(l => ({ ...l, type: "table" })),
    ...packageLeads.map(l => ({ ...l, type: "package" }))
  ]), [tableLeads, packageLeads]);

  const hasAnyData = combinedLeads.length > 0;

  // --- API calls ---
  const fetchPackageBids = useCallback(async (lat, lng, userId) => {
    try {
      const formData = new FormData();
      formData.append('latitude', lat);
      formData.append('longitude', lng);
      formData.append('user_id', userId);

      const { data } = await axios.post(
        `${API_BASE_URL}/master/APIs/ClubPackage/getGroupBookingUserBids`,
        formData
      );
      setPackageData(data.status ? data.data || [] : []);
    } catch {
      setPackageData([]);
    }
  }, []);




  useEffect(() => {
    // Open a connection to CI4 SSE endpoint
    const eventSource = new EventSource(
      `${API_BASE_URL}/master/APIs/ClubPackage/bidsStream/${userdata?.id}`
    );

    eventSource.onmessage = (event) => {
      const payload = JSON.parse(event.data);
      if (payload.status) {
        setPackageData(payload.data);
      }
    };

    eventSource.onerror = (err) => {
      console.error("SSE error:", err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [userdata?.id]);



  const fetchTableBids = useCallback(async (lat, lng, userId) => {
    try {
      const formData = new FormData();
      formData.append('latitude', lat);
      formData.append('longitude', lng);
      formData.append('user_id', userId);

      const { data } = await axios.post(
        `${API_BASE_URL}/master/APIs/ClubTable/getUserBids`,
        formData
      );
      setTableData(data.status ? data.data || [] : []);
    } catch {
      setTableData([]);
    }
  }, []);

  // --- Location + fetch ---
  useEffect(() => {
    if (!userdata?.id) return;
    if (!("geolocation" in navigator)) {
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const latStr = latitude.toString();
        const lngStr = longitude.toString();
        try {
          setLoading(true);
          await Promise.all([
            fetchPackageBids(latStr, lngStr, userdata.id),
            fetchTableBids(latStr, lngStr, userdata.id),
          ]);
        } finally {
          setLoading(false);
        }
      },
      () => {
        alert("Please enable Geo Location Service to find events in your locality.");
        setLoading(false);
      }
    );
  }, [userdata?.id, fetchPackageBids, fetchTableBids]);

  // --- Skeleton ---
  const renderBidSkeleton = (count) =>
    Array.from({ length: count }).map((_, i) => <BidCardSkeleton key={i} />);

  // --- Get current bids based on active lead ---
  const currentBids = useMemo(() => {
    if (!activeLead?.id || !activeLead?.type) return [];
    if (activeLead.type === "package") {
      const pkg = packageData.find(item => item.id === activeLead.id);
      return pkg?.bids || [];
    }
    if (activeLead.type === "table") {
      const tbl = tableData.find(item => item.id === activeLead.id);
      return tbl?.bids || [];
    }
    return [];
  }, [activeLead, packageData, tableData]);

  // --- Bids renderer ---
  const renderBidCards = (bids, isTable, count) => {
    if (!bids.length) return <BidWaitingTimer />;
    return bids.slice(0, count).map((bid, i) => (
      <BidCard key={bid.id || i} data={bid} istableleads={isTable} />
    ));
  };

  // --- Loading ---
  if (loading) {
    return (
      <>
        <div className="upcoming-events-container-web">
          <div className="upcoming-events">
            <div className="upcoming-events-header">
              <Link to="/package-lead-form" style={{ width: '100%' }}>
                <div className="upcoming-events-empty">
                  <img src={noUpcomingEvent} alt="No upcoming events" />
                </div>
              </Link>
            </div>
            {renderBidSkeleton(3)}
          </div>
        </div>
        <div className="upcoming-events-container-mobile">
          <div className="upcoming-events">
            <div className="upcoming-events-header">
              <h3>Loading...</h3>
            </div>
            {/* <div className="upcoming-events-header-bids-mobile">
              {renderBidSkeleton(1)}
            </div> */}
          </div>
        </div>
      </>
    );
  }

  // --- No Data ---
  if (!hasAnyData) {
    return (
      <>
        <div className="upcoming-events-container-web">
          {userdata?.id ? (
            <Link to="/package-lead-form">
              <div className="upcoming-events-empty">
                <img src={noUpcomingEvent} alt="No upcoming events" />
              </div>
            </Link>
          ) : (
            <>
              <LoginRequiredState />
            </>
          )}
        </div>
        <div className="upcoming-events-container-mobile">
          {userdata?.id ? (
            <MobileStateComponent stateType="Nolead" />
          ) : (
            <MobileStateComponent stateType="LoginRequiredState" />
          )}
        </div>
      </>
    );
  }

  const leadData = {
    leadInfo: activeLead.type === "table" ? tableData.find(item => item.id === activeLead.id) : packageData.find(item => item.id === activeLead.id),
    leadType: activeLead.type,
    leadId: activeLead.id
  }



  // --- Render ---
  return (
    <>
      {/* Desktop View */}
      <div className="upcoming-events-container-web">
        <div className="upcoming-events">
          {combinedLeads.length > 0 && (
            <UpcomingEventsCarousel
              leads={combinedLeads}
              setActiveIndex={setActiveLead}
              setModalOpen={setModalOpen}
            />
          )}

          <div className="upcoming-events-header-bids">
            <h3>
              My {activeLead.type === "table" ? "Table" : "Package"} Bids ({currentBids.length})
            </h3>
          </div>
          {renderBidCards(currentBids, activeLead.type === "table", 3)}
          {/* <InvitedState/>
          <AfterCounterBidWaiting/>
          <CounterBidReject/>
          <WaitingForConformationState/>
          <ConfirmByClubAfterPayment/>
          <ReviewState/>
          <AddGuestAfterMenuAdded/>
          <GuestListAddedMenuPanding/>
          <AfterPartyPics/> */}

          {/* ✅ View All - show only if active lead exists */}
          {activeLead?.id && activeLead?.type && currentBids.length > 0 && (
            <div className="upcoming-events-view-all">
              <Link
                to={`/bid-list/${activeLead.id}?source=${activeLead.type}`}
                className="upcoming-events-view-all-link"
              >
                View all
              </Link>
            </div>
          )}

        </div>
      </div>


      {/* Mobile View */}
      <div className="upcoming-events-container-mobile">
        {userdata?.id ? (
          <>
            {combinedLeads.length === 0 ? (
              <MobileStateComponent stateType="Nolead" />
            ) : (
              <MobileStateComponent stateType="LeadBidSate" PreState="bids" combinedLeads={combinedLeads} />
            )}
          </>
        ) : (
          <>
            <MobileStateComponent stateType="LoginRequiredState" />
          </>
        )}

        {/* <div className="upcoming-events">
          <div className="upcoming-events-header" onClick={() => setIsOpenBox(prev => !prev)}>
            <h3>Upcoming Plan</h3>
            <div className="upcoming-events-header-arrow-up-down">
              {isOpenBox ? (
                <FaChevronDown  />
              ) : (
                <FaChevronUp />
              )}
            </div>
          </div>

          {isOpenBox && (
            <>
              <UpcomingEventsCarousel
                leads={combinedLeads}
                setActiveIndex={setActiveLead}
                setModalOpen={setModalOpen}
              />

              <div className="upcoming-events-header-bids">
                <h3>
                  My {activeLead.type === "table" ? "Table" : "Package"} Bids ({currentBids.length})
                </h3>
              </div>
              <div className="upcoming-events-header-bids-mobile">
                {renderBidCards(currentBids, activeLead.type === "table", 1)}
              </div>

              {activeLead?.id && activeLead?.type && currentBids.length > 0 && (
                <div className="upcoming-events-view-all">
                  <Link
                    to={`/bid-list/${activeLead.id}?source=${activeLead.type}`}
                    className="upcoming-events-view-all-link"
                  >
                    <p>View all</p>
                  </Link>
                </div>
              )}
            </>
          )}
        </div> */}
      </div>
    </>
  );
}
